import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable, InjectionToken } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { Router } from '@angular/router';
import { ENVIRONMENT, Environment } from '@environment';
import { getAuth, signInWithCustomToken } from 'firebase/auth';
import firebase from 'firebase/compat/app';
import { catchError, first, from, switchMap, tap, throwError } from 'rxjs';

export const FIREBASE_AUTH_PROVIDER = new InjectionToken(
  'FIREBASE_AUTH_PROVIDER',
  {
    providedIn: 'root',
    factory: () => new firebase.auth.GoogleAuthProvider(),
  }
);

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  constructor(
    private router: Router,
    public fireAuth: AngularFireAuth,
    private http: HttpClient,
    @Inject(FIREBASE_AUTH_PROVIDER)
    private readonly fireAuthProvider: firebase.auth.AuthProvider,
    @Inject(ENVIRONMENT) private env: Environment
  ) {}

  signInWithEmailAndPassword(
    email: string,
    password: string,
    redirectUri: string
  ) {
    return from(this.fireAuth.signInWithEmailAndPassword(email, password)).pipe(
      switchMap(() => this.fireAuth.idToken.pipe(first())),
      switchMap((idToken) => this.getCustomToken(idToken || '')),
      switchMap((customToken) => this.signInWithCustomToken(customToken)),
      catchError(() => {
        this.fireAuth.signOut();
        return throwError(() => new Error('Invalid credential'));
      }),
      tap(() => {
        const redirectToUrl = new URL(redirectUri, location.origin);
        const params = new URLSearchParams(redirectToUrl.search);
        const queryParams: any = {};
        params.forEach((value, key) => (queryParams[key] = value));

        this.router.navigate(['/'], { queryParams });
      })
    );
  }

  signInWithGoogle(redirectUri: string) {
    return from(this.fireAuth.signInWithPopup(this.fireAuthProvider)).pipe(
      switchMap(() => this.fireAuth.idToken.pipe(first())),
      switchMap((idToken) => this.getCustomToken(idToken || '')),
      switchMap((customToken) => this.signInWithCustomToken(customToken)),
      catchError(() => {
        this.fireAuth.signOut();
        return throwError(() => new Error('Invalid credential'));
      }),
      tap(() => {
        const redirectToUrl = new URL(redirectUri, location.origin);
        const params = new URLSearchParams(redirectToUrl.search);
        const queryParams: any = {};
        params.forEach((value, key) => (queryParams[key] = value));

        this.router.navigate(['/'], { queryParams });
      })
    );
  }
  private getCustomToken(idToken: string) {
    const httpOptions = {
      responseType: 'text' as const,
      headers: new HttpHeaders().set('Authorization', `Bearer ${idToken}`),
    };

    return this.http.post(`${this.env.apiPath}/auth/login`, {}, httpOptions);
  }
  private signInWithCustomToken(customToken: string) {
    return from(signInWithCustomToken(getAuth(), customToken));
  }

  sendPasswordResetEmail(email: string) {
    return this.fireAuth.sendPasswordResetEmail(email);
  }
  resetPasswordEmail(email: any) {
    const httpOptions = {
      params: email,
    };
    return this.http.post(`${this.env.apiPath}/auth/resetPassword`, httpOptions.params);
  }

  resetPassword(uuidFirebase: any, checkToken: any, newPassword: any): any {
    const httpOptions = {
      params: { checkToken, userUid: uuidFirebase, pwd: newPassword },
    };
    return this.http.put(`${this.env.apiPath}/auth/updatePassword`, httpOptions.params);
  }

  updatePassword(newPwd: string) {
    return this.http.put(`${this.env.apiPath}/user/updatePassword`, newPwd);
  }

  logout() {
    this.http.post(`${this.env.apiPath}/auth/logout`, {}).subscribe();
    this.fireAuth.signOut();
    location.reload();
  }

  // TO-DO: forse sarà da eliminare in quanto è stato gestito con la function @validatePasswordWithLogin
  validatePassword(providedPassword: string) {
    const user = this.currentUser;
    const credential = firebase.auth.EmailAuthProvider.credential(user?.email || '', providedPassword);
    return user?.reauthenticateWithCredential(credential);
  }
//TODO fix
  validatePasswordWithLogin(providedPassword: string):any {
    // const emailUser = this.providerDataUser?.email;
    // return emailUser ? this.signInWithEmailAndPassword(emailUser, providedPassword, false) : throwError(() => new Error('User not firebase'));
  }

  get currentUser() {
    return firebase.auth().currentUser;
  }

  get providerDataUser() {
    return this.currentUser?.providerData.find((pr: any) => pr.providerId === 'password');
  }
}

